import Image from "next/image";

import { Play } from "lucide-react";

import { cn } from "@/lib/utils";

import type { Testimonial } from "@/lib/constants/marketing-items";

interface ReviewCardProps {
  testimonial: Testimonial;
  onPlayVideo?: (videoUrl: string) => void;
  className?: string;
}

function getInitials(name: string): string {
  return name
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}

export default function ReviewCard({ testimonial, onPlayVideo, className }: ReviewCardProps) {
  const title = testimonial.author ?? "Rebolt Customer";
  const subtitle = testimonial.business ?? "Home Service Pro";
  const initials = testimonial.author
    ? getInitials(testimonial.author)
    : testimonial.business
      ? getInitials(testimonial.business)
      : "RC";

  return (
    <div className={cn("bg-white border border-ash-100 rounded-2xl p-6 w-full flex flex-col gap-4", className)}>
      <span className="text-honey-400 text-sm tracking-[1px] leading-none">★★★★★</span>

      <p className="text-clay-600 text-sm italic leading-normal">&ldquo;{testimonial.content}&rdquo;</p>

      {testimonial.videoUrl && testimonial.thumbnailUrl && (
        <button
          onClick={() => onPlayVideo?.(testimonial.videoUrl!)}
          className="relative w-full h-62 rounded-xl overflow-hidden group cursor-pointer"
        >
          <Image src={testimonial.thumbnailUrl} alt={title} fill className="object-cover" />
          <div className="absolute inset-0 bg-ash-800/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-ash-600/60 border-2 border-ash-500 backdrop-blur-sm flex items-center justify-center group-hover:bg-ash-600/80 transition-colors">
              <Play className="w-6 h-6 text-white ml-0.5" />
            </div>
          </div>
        </button>
      )}

      <div className="flex items-center gap-3 border-t border-ash-200 pt-4">
        <div className="w-[34px] h-[34px] rounded-full bg-ash-100 flex items-center justify-center shrink-0">
          <span className="text-clay-600 text-xs font-bold">{initials}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-ash-900 text-sm font-bold leading-none">{title}</span>
          <span className="text-clay-400 text-xs leading-none">{subtitle}</span>
        </div>
      </div>
    </div>
  );
}
